<template>
  <span>
    <v-btn
      v-if="podePlanejar"
      outlined
      height="39"
      class="ma-1 text-none font-weight-bold"
      :disabled="disabled"
      @click="abrir"
    >
      <v-icon left>mdi-map-marker-plus</v-icon>Encaixar pedido do dia
    </v-btn>
    <v-dialog v-model="dialog" persistent max-width="1100" scrollable>
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span>Encaixar pedido nos roteiros do dia</span>
          <v-btn icon aria-label="Fechar" :disabled="ocupado" @click="fechar"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-card-title>
        <v-card-text>
          <p>
            Escolha um pedido excedente para comparar os roteiros de
            {{ dataFormatada }}. A ordem dos atendimentos existentes será
            preservada.
          </p>
          <v-alert type="info" text dense>
            A posição estimada usa o último atendimento registrado; durante um
            atendimento, o encaixe começa após sua conclusão prevista. A
            confirmação recalcula os horários e mantém a equipe do roteiro.
          </v-alert>
          <v-alert v-if="erro" type="error" text role="alert">{{
            erro
          }}</v-alert>
          <v-alert v-if="recibo" type="success" text role="status">
            Pedido {{ recibo.pedido }} incluído no roteiro
            {{ recibo.roteiro }} · {{ recibo.placa }}.
          </v-alert>
          <template v-if="!recibo">
            <v-autocomplete
              v-model="pedido"
              :items="pedidos"
              item-value="id"
              item-text="rotulo"
              item-disabled="indisponivel"
              label="Pedido pendente"
              :loading="carregando"
              :disabled="ocupado || !diaAtual"
              clearable
              no-data-text="Nenhum pedido pendente encontrado"
              @change="limparPrevia"
            >
              <template #item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.rotulo }}</v-list-item-title>
                  <v-list-item-subtitle>{{
                    item.elegivel
                      ? `${item.origem} → ${item.destino || "Assistência"}`
                      : item.motivo
                  }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
            <v-btn
              color="primary"
              class="text-none mb-4"
              :loading="calculando"
              :disabled="ocupado || !pedido"
              @click="buscar"
            >
              Buscar melhor encaixe
            </v-btn>
            <template v-if="previa">
              <v-alert v-if="!previa.opcoes.length" type="warning" text>
                Nenhum encaixe viável foi encontrado nos roteiros ativos. O
                pedido continua pendente.
              </v-alert>
              <template v-else>
                <p>
                  Opções ordenadas pelo menor acréscimo de quilômetros, com
                  desempate pelo tempo adicional. Revise e confirme o roteiro
                  escolhido. A prévia vale por cinco minutos.
                </p>
                <v-radio-group v-model="roteiro" :disabled="ocupado">
                  <v-radio
                    v-for="(opcao, index) in previa.opcoes"
                    :key="opcao.roteiroId"
                    :value="opcao.roteiroId"
                    class="mb-3"
                  >
                    <template #label>
                      <span>
                        <strong
                          >{{ index === 0 ? "Sugerido · " : "" }}Roteiro
                          {{ opcao.roteiro }} · {{ opcao.placa }}</strong
                        >
                        <span class="d-block"
                          >{{ numero(opcao.resultado.kmAdicionais) }} km
                          adicionais ·
                          {{ numero(opcao.resultado.minutosAdicionais) }} min
                          adicionais · retorno
                          {{ horario(opcao.resultado.retornoSegundos) }}</span
                        >
                      </span>
                    </template>
                  </v-radio>
                </v-radio-group>
                <v-data-table
                  v-if="opcaoAtual"
                  :headers="headers"
                  :items="
                    opcaoAtual.resultado.paradas.filter(
                      (p) => p.tipo !== 'CONCLUSAO_ASSISTENCIA'
                    )
                  "
                  item-key="no"
                  :items-per-page="10"
                  dense
                >
                  <template #item.pedido="{ item }">
                    {{ item.pedido }}
                    <v-chip
                      v-if="item.pedido === pedido"
                      x-small
                      color="primary"
                      >Novo</v-chip
                    >
                  </template>
                  <template #item.inicioSegundos="{ item }">{{
                    horario(item.inicioSegundos)
                  }}</template>
                </v-data-table>
                <p v-if="!podeAprovar" class="mt-3">
                  Seu acesso permite consultar o encaixe. A confirmação exige
                  permissão para alterar pedidos, roteiros e trajetos e inserir
                  trajetos.
                </p>
              </template>
              <v-expansion-panels
                v-if="previa.indisponiveis.length"
                class="mt-4"
                flat
              >
                <v-expansion-panel>
                  <v-expansion-panel-header
                    >Roteiros sem encaixe ({{
                      previa.indisponiveis.length
                    }})</v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    <p
                      v-for="item in previa.indisponiveis"
                      :key="item.roteiroId"
                    >
                      Roteiro #{{ item.roteiroId }}: {{ item.motivo }}
                    </p>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </template>
          </template>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn v-if="recibo" outlined class="text-none" @click="abrir"
            >Encaixar outro pedido</v-btn
          >
          <v-spacer />
          <v-btn text class="text-none" :disabled="ocupado" @click="fechar"
            >Fechar</v-btn
          >
          <v-btn
            v-if="!recibo"
            color="primary"
            class="text-none"
            :loading="confirmando"
            :disabled="ocupado || !opcaoAtual || !podeAprovar"
            @click="confirmar"
            >Confirmar encaixe</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import DataService from "@/services/ComumDataService";
const url = "/roteirizador/pedidos";
export default {
  name: "EncaixarPedidoDia",
  props: {
    obterData: { type: Function, required: true },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      dialog: false,
      data: "",
      pedidos: [],
      pedido: null,
      previa: null,
      roteiro: null,
      recibo: null,
      erro: "",
      carregando: false,
      calculando: false,
      confirmando: false,
      token: 0,
      headers: [
        { text: "Pedido", value: "pedido" },
        { text: "Atendimento", value: "tipo" },
        { text: "Local", value: "descricao" },
        { text: "Nova previsão", value: "inicioSegundos" },
      ],
    };
  },
  computed: {
    tenantId() {
      return this.$store.state.inquilino?.id;
    },
    permissoes() {
      return this.$store.state.permissoes || [];
    },
    podePlanejar() {
      return ["Roteirizador_PlanejamentoRotas", "Pedido_List"].every((p) =>
        this.permissoes.includes(`ROLE_${p}`)
      );
    },
    podeAprovar() {
      return [
        "Pedido_Update",
        "Roteiro_Update",
        "RoteiroTrajeto_Insert",
        "RoteiroTrajeto_Update",
      ].every((p) => this.permissoes.includes(`ROLE_${p}`));
    },
    ocupado() {
      return this.carregando || this.calculando || this.confirmando;
    },
    diaAtual() {
      return (
        this.data ===
        new Intl.DateTimeFormat("sv-SE", {
          timeZone: "America/Sao_Paulo",
        }).format(new Date())
      );
    },
    dataFormatada() {
      return this.data.split("-").reverse().join("/");
    },
    opcaoAtual() {
      return (
        this.previa?.opcoes.find((o) => o.roteiroId === this.roteiro) || null
      );
    },
  },
  watch: {
    tenantId() {
      this.fechar();
    },
  },
  beforeDestroy() {
    this.token++;
  },
  deactivated() {
    this.fechar();
  },
  methods: {
    limparPrevia() {
      this.previa = null;
      this.roteiro = null;
      this.erro = "";
    },
    fechar() {
      this.token++;
      this.dialog = false;
      this.carregando = false;
      this.calculando = false;
      this.confirmando = false;
      this.limparPrevia();
      this.recibo = null;
      this.pedidos = [];
      this.pedido = null;
    },
    mensagem(e) {
      return (
        e.response?.data?.message ||
        e.message ||
        "Não foi possível concluir o encaixe."
      );
    },
    numero(n) {
      return Number(n).toLocaleString("pt-BR", { maximumFractionDigits: 1 });
    },
    horario(seconds) {
      const d = new Date(`${this.data}T00:00:00Z`);
      d.setUTCSeconds(seconds);
      return `${String(d.getUTCDate()).padStart(2, "0")}/${String(
        d.getUTCMonth() + 1
      ).padStart(2, "0")} ${String(d.getUTCHours()).padStart(2, "0")}:${String(
        d.getUTCMinutes()
      ).padStart(2, "0")}`;
    },
    async abrir() {
      this.fechar();
      this.dialog = true;
      this.data =
        this.obterData() ||
        new Intl.DateTimeFormat("sv-SE", {
          timeZone: "America/Sao_Paulo",
        }).format(new Date());
      if (!this.diaAtual) {
        this.erro =
          "Selecione a data de hoje na listagem para encaixar pedidos do dia.";
        return;
      }
      const token = this.token;
      const tenant = this.tenantId;
      this.carregando = true;
      try {
        const { data } = await DataService.get(url, {
          params: { data: this.data },
        });
        if (token !== this.token || tenant !== this.tenantId) return;
        this.pedidos = data.map((p) => ({
          ...p,
          rotulo: `#${p.id} · ${p.cliente}`,
          indisponivel: !p.elegivel,
        }));
      } catch (e) {
        if (token === this.token && tenant === this.tenantId)
          this.erro = this.mensagem(e);
      } finally {
        if (token === this.token) this.carregando = false;
      }
    },
    async buscar() {
      if (this.ocupado || !this.pedido) return;
      this.limparPrevia();
      this.calculando = true;
      const token = this.token;
      const tenant = this.tenantId;
      try {
        const { data } = await DataService.post(`${url}/encaixes`, {
          pedido: this.pedido,
        });
        if (token !== this.token || tenant !== this.tenantId) return;
        this.previa = data;
        this.roteiro = data.opcoes[0]?.roteiroId || null;
      } catch (e) {
        if (token === this.token && tenant === this.tenantId)
          this.erro = this.mensagem(e);
      } finally {
        if (token === this.token) this.calculando = false;
      }
    },
    async confirmar() {
      if (this.recibo || this.ocupado || !this.opcaoAtual || !this.podeAprovar)
        return;
      this.erro = "";
      this.confirmando = true;
      const token = this.token;
      const tenant = this.tenantId;
      try {
        const { data } = await DataService.post(
          `${url}/encaixes/${this.previa.id}/aprovacao`,
          { roteiroId: this.roteiro }
        );
        if (token !== this.token || tenant !== this.tenantId) return;
        this.recibo = data;
        this.$emit("gerado", data);
      } catch (e) {
        if (token !== this.token || tenant !== this.tenantId) return;
        if (e.response?.status === 409) this.limparPrevia();
        this.erro = this.mensagem(e);
      } finally {
        if (token === this.token) this.confirmando = false;
      }
    },
  },
};
</script>
